/**
 * Una pagina del BO ne apre un'altra (es. scheda prodotto -> Nuova marca) e deve farsi
 * riportare indietro con quello che aveva gia' compilato e con il risultato.
 *
 *  - la pagina di partenza chiama `openWithReturn`: salva la sua bozza e naviga alla
 *    pagina figlia con `?from=<token>` nell'URL;
 *  - la pagina figlia legge `returnInfo($route)`: se c'e' un token, Annulla e Salva tornano
 *    alla pagina di partenza (`goBackWithResult`), altrimenti si comporta come sempre
 *    (es. Nuova marca aperta dalla lista Marche torna alla lista);
 *  - la pagina di partenza, quando rinasce, legge `takeResume`: bozza e risultato tornano
 *    solo se arriva davvero dalla pagina figlia, cosi' una bozza rimasta in sospeso non
 *    ricompare mai in un'apertura normale.
 *
 * Tutto sta in memoria e in sessionStorage (pageHandoff): regge anche un refresh in mezzo.
 */
import { putHandoff, takeHandoff } from './pageHandoff';

const KEY = 'pageReturn:';
const memory = {};

function saveInfo(token, info) {
	memory[token] = info;
	try {
		sessionStorage.setItem(KEY + token, JSON.stringify(info));
	} catch (e) {
		// niente sessionStorage: basta la memoria
	}
}

function readInfo(token) {
	if (memory[token]) return memory[token];
	try {
		return JSON.parse(sessionStorage.getItem(KEY + token) || 'null');
	} catch (e) {
		return null;
	}
}

function dropInfo(token) {
	delete memory[token];
	try {
		sessionStorage.removeItem(KEY + token);
	} catch (e) {
		// niente sessionStorage
	}
}

const pathOf = fullPath => String(fullPath || '').split('?')[0];

/**
 * Apre `location` ({ path, query }) ricordando la pagina attuale come ritorno.
 * `draft`: quello che la pagina vuole ritrovare al ritorno; `label`: testo della freccia.
 */
export function openWithReturn(vm, location, { draft = null, label = 'Torna indietro' } = {}) {
	const token = Math.random().toString(36).slice(2, 10);
	const origin = vm.$route.fullPath;
	saveInfo(token, { origin, label, child: location.path });
	// solo chi ha qualcosa da ritrovare (es. una scheda compilata a meta') lascia una bozza
	if (draft) putHandoff('draft:' + origin, { draft, child: location.path });
	vm.$router.push({ path: location.path, query: { ...(location.query || {}), from: token } }).catch(() => {});
}

/** Nella pagina figlia: chi l'ha aperta, oppure null se e' un'apertura normale. */
export function returnInfo(route) {
	const token = route.query && route.query.from;
	if (!token) return null;
	const info = readInfo(token);
	return info ? { ...info, token } : null;
}

/**
 * Nella pagina figlia: torna alla pagina di partenza consegnandole `result` (o null per
 * Annulla). `enteredFrom` e' il path da cui la figlia e' stata aperta: se e' la pagina di
 * partenza si usa "indietro", cosi' la cronologia resta pulita; dopo un refresh si naviga.
 */
export function goBackWithResult(router, info, enteredFrom, result = null) {
	putHandoff('result:' + info.origin, { result, child: info.child });
	dropInfo(info.token);
	if (pathOf(enteredFrom) === pathOf(info.origin)) {
		router.back();
	} else {
		router.push(info.origin).catch(() => {});
	}
}

/**
 * Nella pagina di partenza, quando rinasce: { draft, result } se si torna dalla pagina
 * figlia, altrimenti null. Va chiamata sempre: consuma comunque cio' che e' rimasto.
 */
export function takeResume(route, enteredFrom) {
	const fullPath = route.fullPath;
	const pending = takeHandoff('draft:' + fullPath);
	const returned = takeHandoff('result:' + fullPath);
	if (!pending || pathOf(enteredFrom) !== pending.child) return null;
	return { draft: pending.draft, result: returned ? returned.result : null };
}
